import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PromoGroup } from '@/api/entities';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Plus, 
  Edit, 
  Trash2, 
  Tag,
  ArrowUp,
  ArrowDown,
  Palette
} from "lucide-react";
import PromoGroupForm from './PromoGroupForm';

export default function PromoGroupsSettings() {
  const { t } = useTranslation();
  const [promoGroups, setPromoGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  useEffect(() => {
    loadPromoGroups();
  }, []);

  const loadPromoGroups = async () => {
    setIsLoading(true);
    try {
      const groups = await PromoGroup.list('priority');
      
      // Validate that all groups have valid UUID IDs
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      const invalidGroups = groups.filter(group => !group.id || !uuidRegex.test(group.id));
      
      if (invalidGroups.length > 0) {
        console.error('Found promo groups with invalid IDs:', invalidGroups);
      }
      
      setPromoGroups(groups);
    } catch (error) {
      console.error("Error loading promo groups:", error);
    }
    setIsLoading(false);
  };

  const handleCreate = () => {
    setEditingGroup(null);
    setIsFormOpen(true);
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setIsFormOpen(true);
  };

  const handleDelete = async (group) => {
    if (!confirm(t('promo.delete_confirm', { name: group.name }))) {
      return;
    }

    // Validate that group.id is a valid UUID
    if (!group.id || typeof group.id !== 'string') {
      console.error("Invalid group ID:", group.id);
      alert(t('promo.invalid_id'));
      return;
    }

    // UUID validation regex
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(group.id)) {
      console.error("Invalid UUID format for group ID:", group.id);
      alert(t('promo.invalid_id_format'));
      return;
    }

    try {
      await PromoGroup.delete(group.id);
      loadPromoGroups();
    } catch (error) {
      console.error("Error deleting promo group:", error);
      
      // Provide more specific error messages
      if (error.message && error.message.includes('pattern')) {
        alert(t('promo.invalid_id_refresh'));
      } else if (error.message && error.message.includes('not found')) {
        alert("Ошибка: промо-группа не найдена. Возможно, она была уже удалена.");
      } else {
        alert(t('promo.delete_group_error') + ": " + (error.message || t('promo.unknown_error')));
      }
    }
  };

  const handleFormSave = async () => {
    setIsFormOpen(false);
    setEditingGroup(null);
    loadPromoGroups();
  };

  const updatePriority = async (groupId, newPriority) => {
    // Validate that groupId is a valid UUID
    if (!groupId || typeof groupId !== 'string') {
      console.error("Invalid group ID:", groupId);
      return;
    }

    // UUID validation regex
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(groupId)) {
      console.error("Invalid UUID format for group ID:", groupId);
      return;
    }

    try {
      await PromoGroup.update(groupId, { priority: newPriority });
      loadPromoGroups();
    } catch (error) {
      console.error("Error updating priority:", error);
    }
  };

  const toggleActive = async (group) => {
    // Validate that group.id is a valid UUID
    if (!group.id || typeof group.id !== 'string') {
      console.error("Invalid group ID:", group.id);
      return;
    }

    // UUID validation regex
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(group.id)) {
      console.error("Invalid UUID format for group ID:", group.id);
      return;
    }

    try {
      await PromoGroup.update(group.id, { is_active: !group.is_active });
      loadPromoGroups();
    } catch (error) {
      console.error("Error toggling active status:", error);
    }
  };

  const getFontWeightDisplay = (weight) => {
    switch (weight) {
      case 'normal': return 'Обычный';
      case 'semibold': return 'Полужирный';
      case 'bold': return 'Жирный';
      default: return weight;
    }
  };

  if (isLoading) {
    return (
      <Card className="elegant-card">
        <CardContent className="p-6 text-center">
          Загрузка промо-групп...
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="elegant-card">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-amber-600" />
              {t('promo.product_groups')}
            </CardTitle>
            <Button onClick={handleCreate} className="bg-amber-600 hover:bg-amber-700">
              <Plus className="w-4 h-4 mr-2" />
              {t('promo.create_group')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 mb-6">
            {t('promo.description')}
          </p>

          {promoGroups.length === 0 ? (
            <div className="text-center py-12">
              <Tag className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                              <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('promo.not_created')}</h3>
              <p className="text-slate-500 mb-4">{t('promo.create_first_group_desc')}</p>
              <Button onClick={handleCreate} className="bg-amber-600 hover:bg-amber-700">
                <Plus className="w-4 h-4 mr-2" />
                {t('promo.create_first_group')}
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('promo.priority')}</TableHead>
                  <TableHead>{t('promo.name')}</TableHead>
                  <TableHead>{t('promo.header_style')}</TableHead>
                  <TableHead>{t('promo.card_size')}</TableHead>
                  <TableHead>{t('promo.status')}</TableHead>
                  <TableHead className="text-right">{t('promo.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {promoGroups.map((group, index) => (
                  <TableRow key={group.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="min-w-8 justify-center">
                          {group.priority}
                        </Badge>
                        <div className="flex flex-col gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0"
                            onClick={() => updatePriority(group.id, group.priority - 1)}
                            disabled={index === 0}
                          >
                            <ArrowUp className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0"
                            onClick={() => updatePriority(group.id, group.priority + 1)}
                            disabled={index === promoGroups.length - 1}
                          >
                            <ArrowDown className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-slate-900">{group.name}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded border"
                          style={{ backgroundColor: group.text_color }}
                        />
                        <span className="text-sm text-slate-600">
                          {group.font_size}px, {getFontWeightDisplay(group.font_weight)}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-purple-100 text-purple-800">
                        {group.card_variant || 'small'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch 
                          checked={group.is_active}
                          onCheckedChange={() => toggleActive(group)}
                        />
                        <Badge className={group.is_active ? "bg-green-600 text-white border-green-700" : "bg-gray-600 text-white border-gray-700"}>
                          {group.is_active ? t('promo.active') : t('promo.inactive')}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(group)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(group)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <PromoGroupForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        group={editingGroup}
        onSave={handleFormSave}
      />
    </div>
  );
}